/**
 * Utility functions for validating content structure
 */

/**
 * Validates that the provided content matches the expected structure
 * by checking if all required paths exist in the content object
 * 
 * @param content The content object to validate
 * @param schema The expected structure to check against
 * @param currentPath Current path being checked (used for recursion)
 * @returns An object with validation result and missing paths if any
 */
export function validateContentStructure(
  content: any, 
  schema: any, 
  currentPath: string = ''
): { isValid: boolean; missingPaths: string[] } {
  // Initialize result
  const result = { isValid: true, missingPaths: [] as string[] };
  
  // If content is undefined or null but schema expects something, it's invalid
  if ((content === undefined || content === null) && schema !== undefined && schema !== null) {
    result.isValid = false;
    result.missingPaths.push(currentPath || 'root');
    return result;
  }
  
  // If schema is an object, check all its properties
  if (typeof schema === 'object' && schema !== null && !Array.isArray(schema)) {
    for (const key in schema) {
      const newPath = currentPath ? `${currentPath}.${key}` : key;
      
      // If the key doesn't exist in content, mark as invalid
      if (!(key in content)) {
        result.isValid = false;
        result.missingPaths.push(newPath);
        continue;
      }
      
      // Recursively validate nested objects
      const nestedResult = validateContentStructure(content[key], schema[key], newPath);
      
      if (!nestedResult.isValid) {
        result.isValid = false;
        result.missingPaths.push(...nestedResult.missingPaths);
      }
    }
  }
  // If schema is an array, validate its structure if it has items
  else if (Array.isArray(schema) && schema.length > 0) {
    // Check if content is also an array
    if (!Array.isArray(content)) {
      result.isValid = false;
      result.missingPaths.push(`${currentPath} (expected array)`);
      return result;
    } 
    
    // Check if the content array has at least one item to validate against
    if (content.length === 0) {
      // Empty arrays are valid as long as the schema expects an array
      return result;
    }
    
    // If schema has at least one item, use it as a template for validating array items
    if (schema.length > 0) {
      const templateItem = schema[0];
      
      // Validate each content item against the template
      for (let i = 0; i < content.length; i++) {
        const itemPath = `${currentPath}[${i}]`;
        const itemResult = validateContentStructure(content[i], templateItem, itemPath);
        
        if (!itemResult.isValid) {
          result.isValid = false;
          result.missingPaths.push(...itemResult.missingPaths);
        }
      }
    }
  }
  
  return result;
}

/**
 * Validates content against a template schema to ensure all required
 * fields are present before rendering
 * 
 * @param content The content object to validate
 * @param template The template object that defines the expected structure
 * @returns True if valid, throws error with details if invalid
 */
export function validateContent<T>(content: any, template: T): boolean {
  const validationResult = validateContentStructure(content, template);
  
  if (!validationResult.isValid) {
    const errorMessage = `Content validation failed. Missing required paths: ${validationResult.missingPaths.join(', ')}`;
    throw new Error(errorMessage);
  }
  
  return true;
} 